import {
  EnMetricDistrict,
  EnMetricMovement,
  EnMetricSide,
} from '@shared/dto';
import { ROM_GOAL_DEGREES } from './rom-target-editor.constants';
import { RomTargetDraft, RomTargetValue } from './rom-target-editor.types';

const DISTRICTS = new Set<string>(Object.values(EnMetricDistrict));
const SIDES = new Set<string>(Object.values(EnMetricSide));
const MOVEMENTS = new Set<string>(Object.values(EnMetricMovement));

/** Persist identity matching measured ROM (KH-1433). */
export function romTargetKey(
  row: Pick<RomTargetValue, 'district' | 'side' | 'movement'>,
): string {
  return `${row.district}:${row.side}:${row.movement}`;
}

/**
 * JSON numbers sometimes arrive as strings — the same class of lie as
 * `SessionEventDto.start` typed `Date` and arriving ISO. Rejecting the string
 * would drop a real goal with no error; coerce here, at the DTO boundary.
 */
function parseGoalDegrees(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampGoalDegrees(value: number): number {
  return Math.min(ROM_GOAL_DEGREES.MAX, Math.max(ROM_GOAL_DEGREES.MIN, value));
}

function parseRomTargetValue(value: unknown): RomTargetValue | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const row = value as Record<string, unknown>;
  const goalDegrees = parseGoalDegrees(row['goalDegrees']);
  if (
    !DISTRICTS.has(row['district'] as string) ||
    !SIDES.has(row['side'] as string) ||
    !MOVEMENTS.has(row['movement'] as string) ||
    goalDegrees === null
  ) {
    return null;
  }
  return {
    district: row['district'] as EnMetricDistrict,
    side: row['side'] as EnMetricSide,
    movement: row['movement'] as EnMetricMovement,
    goalDegrees: clampGoalDegrees(goalDegrees),
  };
}

export function isRomTargetValue(value: unknown): boolean {
  return parseRomTargetValue(value) !== null;
}

export function isRomGoalDegreesInRange(value: number): boolean {
  return (
    Number.isFinite(value) &&
    value >= ROM_GOAL_DEGREES.MIN &&
    value <= ROM_GOAL_DEGREES.MAX
  );
}

/**
 * A row the backend can store. The list is already sparse — a missing region
 * is simply not a row — so the only persist gate left is the degree range.
 * `setValue` can put a raw number on the control that the editor never clamped.
 */
export function isPersistableRomTarget(row: RomTargetValue): boolean {
  return isRomGoalDegreesInRange(row.goalDegrees);
}

/**
 * A row both the range validator and the triage POST accept.
 * One predicate, so a malformed or out-of-range row cannot be blocked in the
 * form and still mapped — or the other way around.
 */
export function isSubmittableRomTarget(row: unknown): row is RomTargetValue {
  if (!isRomTargetValue(row) || typeof row !== 'object' || row === null) {
    return false;
  }
  const goalDegrees = (row as { goalDegrees?: unknown }).goalDegrees;
  return typeof goalDegrees === 'number' && isRomGoalDegreesInRange(goalDegrees);
}

export function isRomTargetDraftComplete(
  draft: RomTargetDraft,
): draft is RomTargetValue {
  return (
    draft.district !== null &&
    draft.movement !== null &&
    draft.goalDegrees !== null &&
    isRomGoalDegreesInRange(draft.goalDegrees)
  );
}

export function toRomTargetValue(row: RomTargetValue): RomTargetValue {
  return {
    district: row.district,
    side: row.side,
    movement: row.movement,
    goalDegrees: clampGoalDegrees(row.goalDegrees),
  };
}

/**
 * Hosts may patch a partial or dirty array. Invalid rows are dropped; the last
 * write for a district+side+movement key wins. Identity is the full persist
 * key, not district: one region routinely carries several movements (KH-1433).
 * The editor never invents missing combinations — a ROM target list is sparse.
 */
export function normalizeRomTargets(value: unknown): RomTargetValue[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const byKey = new Map<string, RomTargetValue>();
  for (const item of value) {
    const row = parseRomTargetValue(item);
    if (!row) {
      continue;
    }
    byKey.set(romTargetKey(row), row);
  }
  return [...byKey.values()];
}

export function upsertRomTarget(
  rows: RomTargetValue[],
  incoming: RomTargetValue,
): RomTargetValue[] {
  const next = toRomTargetValue(incoming);
  const key = romTargetKey(next);
  const index = rows.findIndex((row) => romTargetKey(row) === key);
  if (index === -1) {
    return [...rows, next];
  }
  return rows.map((row, i) => (i === index ? next : row));
}

/**
 * Unlike `upsertRomTarget` (the composer's Add button, gated on
 * `isRomTargetDraftComplete` before it ever runs), this is the direct
 * row-edit path: the user is typing straight into an already-committed row.
 * It does NOT clamp — an out-of-range value must reach the control as-is so
 * `romTargetsRangeValidator` can flag it and block Save. Clamping here would
 * silently rewrite what the clinician typed with no validation feedback.
 */
export function patchRomTarget(
  rows: RomTargetValue[],
  key: string,
  patch: Partial<Pick<RomTargetValue, 'goalDegrees'>>,
): RomTargetValue[] {
  return rows.map((row) => (romTargetKey(row) === key ? { ...row, ...patch } : row));
}

export function removeRomTarget(
  rows: RomTargetValue[],
  key: string,
): RomTargetValue[] {
  return rows.filter((row) => romTargetKey(row) !== key);
}

/** The three axes measured ROM already uses (KH-1433), plus the goal itself. */
export type RomTargetIdentity = Pick<
  RomTargetValue,
  'district' | 'side' | 'movement' | 'goalDegrees'
>;

export interface RomTargetDiffPlan<T> {
  create: T[];
  update: Array<{ id: number; goalDegrees: number }>;
  delete: number[];
}

/**
 * Diffs the editor's current rows against what the server holds, keyed on
 * district+side+movement. Shared by the member-profile tab's save and the
 * triage wizard's retry — both need the exact same answer to "what changed",
 * and hand-rolling it twice let them silently diverge: an out-of-range edit
 * to an already-saved goal was a safe no-op on one page and a DELETE on the
 * other, for the identical user action.
 *
 * A row missing from the editor is a delete, not an omission — presence is
 * checked against every row currently in the editor, not just the
 * range-valid ones, so an out-of-range edit to an existing goal is a no-op
 * (the row is still "there", just not writable yet) rather than a deletion.
 * An out-of-range NEW row is neither created nor deleted — there is nothing
 * on the server for it yet. A changed goal on an existing row is an update;
 * an unchanged one produces no call at all.
 */
export function diffRomTargets<T extends RomTargetIdentity>(
  saved: Array<RomTargetIdentity & { id: number }>,
  rows: T[],
): RomTargetDiffPlan<T> {
  const savedByKey = new Map(saved.map((target) => [romTargetKey(target), target]));
  const presentKeys = new Set(rows.map((row) => romTargetKey(row)));
  const persistable = rows.filter((row) => isRomGoalDegreesInRange(row.goalDegrees));

  const plan: RomTargetDiffPlan<T> = { create: [], update: [], delete: [] };

  for (const row of persistable) {
    const existing = savedByKey.get(romTargetKey(row));
    if (!existing) {
      plan.create.push(row);
      continue;
    }
    if (existing.goalDegrees !== row.goalDegrees) {
      plan.update.push({ id: existing.id, goalDegrees: row.goalDegrees });
    }
  }

  for (const target of saved) {
    if (!presentKeys.has(romTargetKey(target))) {
      plan.delete.push(target.id);
    }
  }

  return plan;
}
